
  import { defineComponent, onMounted, ref, computed, reactive } from 'vue';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';
  import moment from 'moment';
  import Multiselect from '@vueform/multiselect';

  export default defineComponent({
    name: 'active-ads-summary-report',
    components: { Multiselect },
    async setup() {
      const isLoading = ref(false);
      const hasQueried = ref(false);
      const data = ref<{ data: Array<any> } | null>(null);
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();

      const defaultFilters = () => ({
        dateFrom: '',
        dateTo: '',
        areaId: null as number | null,
        categoryId: '' as number | '',
      });

      const selectedItems = ref(defaultFilters());
      const appliedFilters = ref(defaultFilters());

      const translate = (text: string): string => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const areasComputed = computed(() => store.getters.allListAreas || []);
      const adTypesComputed = computed(
        () => store.getters.adTypesAllList || []
      );

      const adTypeTitle = (id) => {
        const adType = adTypesComputed.value.find((type) => type.id === id);
        return adType ? adType.titleAr : id || '-';
      };

      const areaTitle = (id) => {
        const area = areasComputed.value.find((item) => item.id === id);
        return area ? area.titleAr : id;
      };

      const formatNumber = (val) => {
        if (val == null || val === '') return '-';
        return Number(val).toLocaleString();
      };

      /**
       * Report rows enriched with the resolved ad type title so that the
       * table and the excel export share the exact same data.
       */
      const reportRows = computed(() => {
        if (!data.value?.data) return [];
        return data.value.data.map((item) => ({
          ...item,
          categoryName: adTypeTitle(item.categoryId),
        }));
      });

      const totals = computed(() => {
        const rows = reportRows.value;
        if (rows.length === 0) {
          return { count: null, minPrice: null, maxPrice: null };
        }

        const prices = (key: string) =>
          rows
            .map((row) => row[key])
            .filter((price) => price != null && price !== '')
            .map(Number);

        const extreme = (values: number[], pick: 'min' | 'max') =>
          values.reduce(
            (acc: number | null, value) =>
              acc == null || (pick === 'min' ? value < acc : value > acc)
                ? value
                : acc,
            null
          );

        return {
          count: rows.reduce((sum, row) => sum + Number(row.count || 0), 0),
          minPrice: extreme(prices('minPrice'), 'min'),
          maxPrice: extreme(prices('maxPrice'), 'max'),
        };
      });

      const resetFilters = () => {
        selectedItems.value = defaultFilters();
        appliedFilters.value = defaultFilters();
        hasQueried.value = false;
        data.value = null;
      };

      const onSubmitCreate = async () => {
        if (selectedItems.value.dateFrom) {
          selectedItems.value.dateFrom = moment(
            selectedItems.value.dateFrom
          ).format('YYYY-MM-DD');
        }
        if (selectedItems.value.dateTo) {
          selectedItems.value.dateTo = moment(
            selectedItems.value.dateTo
          ).format('YYYY-MM-DD');
        }

        if (
          selectedItems.value.dateFrom &&
          selectedItems.value.dateTo &&
          moment(selectedItems.value.dateTo).isBefore(
            moment(selectedItems.value.dateFrom)
          )
        ) {
          Swal.fire({
            text: translate('dateToMustBeAfterDateFrom'),
            icon: 'warning',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-warning',
            },
          });
          return;
        }

        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        isLoading.value = true;
        hasQueried.value = true;

        try {
          await store.dispatch(
            Actions.GET_ACTIVE_ADS_SUMMARY_REPORT,
            selectedItems
          );
          data.value = store.getters.activeAdsSummaryReportData;
          appliedFilters.value = { ...selectedItems.value };

          if (reportRows.value.length > 0) {
            Swal.fire({
              text: translate('SUCCESSFULLY_GET_REPORTS_DATA'),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          } else {
            Swal.fire({
              text: translate('noDataFound'),
              icon: 'info',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-info',
              },
            });
          }
        } catch (error) {
          const [errorName] = Object.keys(store.getters.getErrors);
          const errorMsg = store.getters.getErrors[errorName];

          Swal.fire({
            text: translate(errorMsg?.[0] || 'errorOccurred'),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } finally {
          isLoading.value = false;
          submitButton.value?.removeAttribute('data-kt-indicator');
          if (submitButton.value) {
            submitButton.value.disabled = false;
          }
        }
      };

      const excelHeader = reactive({
        'Property Type': 'title',
        'Ad Type': 'categoryName',
        'Number Of Ads': 'count',
        'Min Price': 'minPrice',
        'Max Price': 'maxPrice',
      });

      const excelHeaderRows = computed(() => {
        const all = 'الكل';
        const rows: string[] = [];

        const from = appliedFilters.value.dateFrom || '-';
        const to = appliedFilters.value.dateTo || '-';
        rows.push(`التاريخ: من ${from} إلى ${to}`);

        rows.push(
          `المنطقة: ${
            appliedFilters.value.areaId
              ? areaTitle(appliedFilters.value.areaId)
              : all
          }`
        );

        rows.push(
          `نوع الإعلان: ${
            appliedFilters.value.categoryId
              ? adTypeTitle(appliedFilters.value.categoryId)
              : all
          }`
        );

        return rows;
      });

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('activeAdsSummaryReport'), [
          translate('Reports'),
        ]);
      });

      // Load DDL data
      await store.dispatch(Actions.GET_ALL_AREAS_LIST, { type: 'isAd' });
      await store.dispatch(Actions.GET_ALL_AD_TYPES);

      reinitializeComponents();
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        areas: areasComputed,
        adTypes: adTypesComputed,
        excelHeader,
        excelHeaderRows,
        data,
        reportRows,
        totals,
        selectedItems,
        isLoading,
        hasQueried,
        onSubmitCreate,
        resetFilters,
        formatNumber,
        submitButton,
        translate,
        goBack,
        can,
      };
    },
  });
